import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useScoreboard, type ScoreEvent } from "@/hooks/useScoreboard";

function ScoreCard({ g }: { g: ScoreEvent }) {
  const isLive = g.status === "live";
  const time = new Date(g.startTime).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return (
    <li className="snap-start shrink-0 w-64 rounded-xl border border-border bg-card px-4 py-3 shadow-sm">
      <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider mb-2">
        <span className="text-primary">{g.league}</span>
        {isLive ? (
          <span className="flex items-center gap-1 text-red-500">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 motion-safe:animate-pulse" />
            LIVE {g.period ?? ""}
          </span>
        ) : (
          <span className="text-muted-foreground">{time}</span>
        )}
      </div>
      <div className="space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="truncate pr-2">{g.away.name}</span>
          <span className="font-bold tabular-nums">{g.awayScore ?? "—"}</span>
        </div>
        <div className="flex justify-between">
          <span className="truncate pr-2">{g.home.name}</span>
          <span className="font-bold tabular-nums">{g.homeScore ?? "—"}</span>
        </div>
      </div>
    </li>
  );
}

const ScoresTicker = () => {
  const { data, isLoading } = useScoreboard();
  const railRef = useRef<HTMLUListElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  // Only live or still-upcoming events belong in the ticker.
  const now = Date.now();
  const games = (data?.events ?? [])
    .filter(
      (g) =>
        g.status === "live" ||
        (g.status === "scheduled" && new Date(g.startTime).getTime() > now - 60 * 60 * 1000),
    )
    .slice(0, 12);

  const syncArrows = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  }, []);

  useEffect(() => {
    syncArrows();
    const el = railRef.current;
    if (!el) return;
    el.addEventListener("scroll", syncArrows, { passive: true });
    window.addEventListener("resize", syncArrows);
    return () => {
      el.removeEventListener("scroll", syncArrows);
      window.removeEventListener("resize", syncArrows);
    };
  }, [syncArrows, games.length, isLoading]);

  const scrollBy = (dir: 1 | -1) => {
    const el = railRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.8, 240), behavior: "smooth" });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      scrollBy(1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      scrollBy(-1);
    }
  };

  // Hidden completely while loading or when nothing is live or upcoming.
  if (games.length === 0) return null;

  return (
    <section className="border-y border-border bg-muted/30" aria-labelledby="scores-ticker-heading">
      <div className="container py-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2
            id="scores-ticker-heading"
            className="text-xs font-semibold uppercase tracking-[0.2em] text-primary"
          >
            Live &amp; upcoming
          </h2>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              disabled={!canPrev}
              aria-label="Scroll scores left"
              className="h-11 w-11 inline-flex items-center justify-center rounded-full border border-border bg-background disabled:opacity-40 hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              disabled={!canNext}
              aria-label="Scroll scores right"
              className="h-11 w-11 inline-flex items-center justify-center rounded-full border border-border bg-background disabled:opacity-40 hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
            <Link
              to="/scores"
              className="ml-2 inline-flex items-center h-11 px-2 text-xs font-semibold hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
            >
              View all →
            </Link>
          </div>
        </div>
        <ul
          ref={railRef}
          tabIndex={0}
          role="list"
          aria-label="Live and upcoming games"
          onKeyDown={onKeyDown}
          className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 scrollbar-thin focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
        >
          {games.map((g) => (
            <ScoreCard key={g.id} g={g} />
          ))}
        </ul>
      </div>
    </section>
  );
};

export default ScoresTicker;
